/**
 * Shrinking a photograph so the segmentation model will accept it.
 *
 * **This exists because a full-resolution phone photograph is rejected outright.** Bedrock's background
 * removal caps input at 9,437,184 pixels (3072²). An iPhone shoots 4032 × 3024, which is 12,192,768 — a
 * third over — so every real capture came back as a validation error before inference began. The
 * symptom was indistinguishable from a bad photo, so the app told the family to retake it, and retaking
 * produced the same pixel count.
 *
 * The original is still uploaded untouched, because requirement 7.2 keeps it forever so the whole garage
 * can be reprocessed against a better model with no re-shooting. This is a *second*, disposable object
 * alongside it.
 *
 * Done in the browser rather than in the Lambda for one concrete reason: the cutout function has no JPEG
 * decoder. It passes the photograph's bytes to Bedrock untouched and only decodes the PNG that comes
 * back, which is what keeps it free of an image library and keeps `terraform apply` the entire deploy.
 * The browser already has a decoder, a scaler and an encoder built in.
 */

/**
 * Longest edge to send for segmentation, mirrored from `media.INFERENCE_MAX_EDGE`.
 *
 * Bedrock imposes two ceilings and the binding one is not the obvious one. The input cap is 9,437,184
 * pixels, which a 12-megapixel photo exceeds by a third — but downscaling to 5.9 megapixels then hit a
 * *response* limit instead, because the model returns a PNG and that cannot exceed 16 MiB. Measured:
 *
 *     2800 × 2100 → 16.9 MB  refused
 *     2048 × 1536 →  7.1 MB  fine
 *     1600 × 1200 →  4.5 MB  fine
 *
 * 2048 leaves better than a 2× margin and costs nothing real: the cutout is composited on a 1200 × 720
 * canvas and the Lambda never upscales, so detail beyond about 1200px across the truck is discarded one
 * step later anyway.
 */
export const INFERENCE_MAX_EDGE = 2048;

const QUALITY = 0.92;

/**
 * A JPEG small enough to segment, or `null` when the original already is.
 *
 * Returning `null` is the normal answer for a screenshot or a photo from an older phone, and the API
 * treats a missing copy as "use the original" — so there is no need for the caller to distinguish
 * "did not need one" from "could not make one". Both mean: send what we have.
 *
 * Never throws. A decode failure means no copy, which means the cutout may be refused and the vehicle
 * shows requirement 7.7's placeholder — the same outcome as any other cutout failure, and not worth
 * failing an upload the family has already waited for.
 */
export async function downscaleForInference(file: Blob): Promise<Blob | null> {
  try {
    // `from-image` applies the EXIF rotation. Without it a portrait photo is decoded sideways, and the
    // cutout would come out of the canvas step rotated with no obvious cause.
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    try {
      const longest = Math.max(bitmap.width, bitmap.height);
      if (longest <= INFERENCE_MAX_EDGE) return null;

      const scale = INFERENCE_MAX_EDGE / longest;
      const width = Math.max(1, Math.floor(bitmap.width * scale));
      const height = Math.max(1, Math.floor(bitmap.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) return null;
      // Browsers do a decent job here; a hand-rolled box filter in JavaScript would be slower and no
      // better, and unlike the Lambda's canvas step there is no alpha edge at stake yet.
      context.imageSmoothingQuality = "high";
      context.drawImage(bitmap, 0, 0, width, height);

      return dataUrlToBlob(canvas.toDataURL("image/jpeg", QUALITY));
    } finally {
      bitmap.close();
    }
  } catch {
    return null;
  }
}

/**
 * `toDataURL` then decode, rather than `toBlob`.
 *
 * The same reason as in `poster.ts`: `toBlob` is callback-based and on older WebKit can silently never
 * call back for a canvas this size. A base64 round trip on a few megabytes is measurable but always
 * returns, and an upload that hangs is worse than one that takes another moment.
 */
function dataUrlToBlob(dataUrl: string): Blob | null {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return null;
  const binary = atob(dataUrl.slice(comma + 1));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: "image/jpeg" });
}
